/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box, Button, Flex, Input, Stack, Text, Textarea } from '@chakra-ui/react';
import { fetchAPI } from '@/api/fetch';
import { AnalyzeJobRequirementsResult, apiAdminAnalyzeJobRequirements } from '@/api/services';
import { useAdminAnalysis } from '@/store/adminAnalysis';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

type FormValues = {
  jobTitle: string;
  jobDescription: string;
};

export default function JobRequirementsSection() {
  const [submitError, setSubmitError] = useState(false);
  const {
    jobDescription,
    jobRequirements,
    jobPositionTags,
    setJobRequirements,
    setJobPositionTags,
    clearJobRequirements,
    clearResumeAnalysisResult
  } = useAdminAnalysis();

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting, isValid }
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      jobTitle: jobDescription?.job_title || '',
      jobDescription: jobDescription?.job_description || ''
    }
  });

  const handleFormSubmit = async (formData: FormValues) => {
    setSubmitError(false);
    clearResumeAnalysisResult();

    try {
      const response = await fetchAPI<AnalyzeJobRequirementsResult>({
        request: () =>
          apiAdminAnalyzeJobRequirements({
            payload: {
              job_title: formData.jobTitle,
              job_description: formData.jobDescription
            }
          })
      });

      if (response.success) {
        setJobRequirements({
          requirements: response.data.requirements,
          nice_to_haves: response.data.nice_to_haves
        });

        setJobPositionTags(response.data.position_tags);
      } else {
        handleError('Failed to analyze job requirements', response.message);
      }
    } catch (error) {
      handleError('Failed to analyze job requirements', error);
    }
  };

  const handleError = (message: string, error: any) => {
    console.error(message, error);
    setSubmitError(true);
  };

  const clear = () => {
    clearResumeAnalysisResult();
    clearJobRequirements();
    reset();
  };

  return (
    <Stack gap="24px">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack gap="16px" mb="24px">
          <Input
            placeholder="Enter job title here..."
            border="1px solid"
            borderColor={errors.jobTitle ? 'error.500' : 'neutrals.200'}
            bg="background.secondary"
            fontSize="16px"
            borderRadius="12px"
            p="16px 24px"
            h="56px"
            {...register('jobTitle', { required: 'Job title is required.' })}
          />
          {errors.jobTitle && (
            <Text fontSize="14px" color="error.700">
              {errors.jobTitle.message}
            </Text>
          )}
        </Stack>
        <Flex h="100%" gap="24px" alignItems="flex-start">
          <Textarea
            p="16px 24px"
            placeholder="Enter job description here..."
            border="1px solid"
            borderColor={errors.jobDescription ? 'error.500' : 'neutrals.200'}
            bg="background.secondary"
            fontSize="16px"
            resize="vertical"
            minH="320px"
            maxH="320px"
            borderRadius="12px"
            {...register('jobDescription', {
              required: 'Job description is required.'
            })}
          />
          <Stack gap="20px">
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={!isValid}
              bg="primary.900"
              fontSize="16px"
              borderRadius="8px"
              p="24px 32px"
              w="150px"
            >
              Analyze
            </Button>
            <Button
              type="button"
              bg="background.secondary"
              color="primary.900"
              fontSize="16px"
              border="1px solid"
              borderColor="primary.900"
              borderRadius="8px"
              p="24px 32px"
              w="150px"
              onClick={clear}
            >
              Cancel
            </Button>
          </Stack>
        </Flex>
        {errors.jobDescription && (
          <Text fontSize="14px" color="error.700" mt="5px">
            {errors.jobDescription.message}
          </Text>
        )}
        {submitError && (
          <Text fontSize="16px" color="error.700" mt="5px">
            Failed to analyze job requirements. Please try again.
          </Text>
        )}
      </form>
      <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
        Analyzed Job Requirements
      </Text>
      {jobRequirements ? (
        <Box w="100%" border="1px solid" borderColor="neutrals.300" borderRadius="12px">
          <Stack p="16px" gap="16px">
            {[
              { label: 'Requirements', value: jobRequirements.requirements },
              { label: 'Nice to have', value: jobRequirements.nice_to_haves }
            ].map(({ label, value }) => (
              <Flex key={label} direction="column" gap="8px">
                <Text fontSize="16px" color="neutrals.800" fontWeight="semibold">
                  {label}:
                </Text>
                {value.map((item, index) => (
                  <Text key={index} fontSize="16px" color="neutrals.800">
                    - {item}
                  </Text>
                ))}
              </Flex>
            ))}
            {jobPositionTags && jobPositionTags.length > 0 && (
              <Flex direction="column" gap="8px">
                <Text fontSize="16px" color="neutrals.800" fontWeight="semibold">
                  Position Tags:
                </Text>
                <Flex wrap="wrap" gap="8px">
                  {jobPositionTags.map((tag, index) => (
                    <Box
                      key={index}
                      bg="primary.100"
                      color="primary.900"
                      px="12px"
                      py="6px"
                      borderRadius="16px"
                      fontSize="14px"
                      fontWeight="medium"
                    >
                      {tag}
                    </Box>
                  ))}
                </Flex>
              </Flex>
            )}
          </Stack>
        </Box>
      ) : (
        <Text fontSize="16px" color="neutrals.700">
          No job requirements analyzed yet.
        </Text>
      )}
    </Stack>
  );
}
